import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoConfig } from '../configs/seoConfig';
import { useSiteConfig } from '../configs/siteConfig';

/**
 * Per-page head tags, rendered as React 19 native elements (React hoists
 * <title>, <meta> and <link> into <head>). No extra library needed.
 *
 * <SEO
 *   title="Contact"                    // becomes "Contact | Projects Kart"
 *   description="Message us …"         // meta description (defaults to the site description)
 *   path="/contact"                    // canonical path; defaults to the current pathname
 *   ogTitle="…" ogDescription="…"      // optional; default to title / description
 *   noindex                            // optional: adds <meta name="robots" content="noindex">
 * />
 *
 * Render it once per page, anywhere in the page's JSX.
 */

const fullTitle = title => {
  if (!title) return seoConfig.defaultTitle;
  return title.includes(seoConfig.siteName)
    ? title
    : `${title} | ${seoConfig.siteName}`;
};

export const canonicalUrl = path => {
  const clean = String(path || '/').split(/[?#]/)[0] || '/';
  return `${seoConfig.siteUrl}${clean.startsWith('/') ? clean : `/${clean}`}`;
};

const SEO = ({
  title,
  description,
  path,
  ogTitle,
  ogDescription,
  noindex = false,
}) => {
  useSiteConfig(); // site name, URL and default texts come from Admin → Settings
  const { pathname } = useLocation();
  const pageTitle = fullTitle(title);
  const pageDescription = description || seoConfig.defaultDescription;
  const canonical = canonicalUrl(path ?? pathname);

  useEffect(() => {
    // public/index.html carries fallback tags for crawlers that don't run
    // JavaScript. Once React manages the head they would only duplicate ours.
    document
      .querySelectorAll('head [data-seo-fallback]')
      .forEach(node => node.remove());

    return () => {
      // React removes this page's <title> when the page unmounts; keep the
      // tab titled if the next page doesn't render <SEO>.
      if (!document.querySelector('head > title')) {
        document.title = seoConfig.defaultTitle;
      }
    };
  }, []);

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={ogTitle || pageTitle} />
      <meta
        property="og:description"
        content={ogDescription || pageDescription}
      />
      <meta property="og:url" content={canonical} />
      {noindex && <meta name="robots" content="noindex" />}
    </>
  );
};

export default SEO;
